/**
 * Debounce function
 * @param {Function} func - The function to debounce
 * @param {boolean} immediate - Whether to execute the function immediately
 * @returns {Function} Debounced function
 */
export function debounce(func, immediate) {
  let timeout
  return function executedFunction() {
    const context = this
    const args = arguments

    const later = () => {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, 500)
    if (callNow) func.apply(context, args)
  }
}

/**
 * appendParams function
 * A function for appending query parameters to the current URL and updating the browser history
 * @param {Object} value - The object containing the parameters to be appended
 */
export function appendParams(value) {
  const params = parseParams(location.search)
  const data = { ...params, ...value }
  Object.keys(data).filter((e) => (data[e] === '' || !data[e]?.toString().length) && delete data[e])
  window.history.pushState({}, null, `${location.pathname}?${new URLSearchParams(data).toString()}`)
}

/**
 * parseParams function
 * A function for parsing query parameters from a given URL string or the current location
 * @param {string} params - The URL string containing the query parameters (default: '')
 * @returns {Object} An object containing the parsed query parameters
 */
export function parseParams(queryString = '') {
  const params = new URLSearchParams(queryString)
  const result = {}

  for (const [key, value] of params.entries()) {
    // Check if the value is supposed to be an array
    if (value.includes(',')) {
      result[key] = value.split(',') // Convert to array
    } else if (!isNaN(value)) {
      // Check if the value can be parsed as an integer
      result[key] = parseInt(value, 10) // Convert to integer
    } else if (value === 'true' || value === 'false') {
      // Check if the value is a boolean
      result[key] = value === 'true' // Convert to boolean
    } else {
      // Otherwise, keep it as a string
      result[key] = value // Keep as string
    }
  }

  return result
}

/**
 * setSortType function
 * @param {Array} data - The array of objects to set sort types for
 * @param {Object} payload - The payload containing the sort information
 * @param {string} payload.sSortBy - The internal name of the column to sort by
 * @param {number} payload.nOrder - The order (ascending or descending) of the sorting
 * @returns {Array} Updated array of objects with sort types set
 */
export function setSortType(data, payload) {
  return data.map((data) => {
    if (data.sInternalName === payload?.sSortBy) data.type = Number(payload?.nOrder)
    else data.type = 0
    return data
  })
}

/**
 * convertObjectToQueryString function
 * A function for converting an object into a query string and a filtered object
 * @param {Object} obj - The object to be converted into a query string
 * @returns {Object} An object containing the query string and the filtered object
 */
export function convertObjectToQueryString(obj) {
  const filteredParams = Object.fromEntries(
    Object.entries(obj).filter(([, value]) => (value && value !== undefined) || value === 0 || typeof value === 'boolean')
  )
  return { queryString: new URLSearchParams(filteredParams).toString(), queryObject: filteredParams }
}

/**
 * formatDate function
 * A function for formatting a date string according to the provided format
 * @param {string} dateString - The date string to be formatted
 * @param {'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYY-M-D' | 'YYYY/M/D' |
 *         'YY-MM-DD' | 'YY/MM/DD' | 'YY-M-D' | 'YY/M/D' |
 *         'MMMM D, YYYY' | 'MMMM D, YY' |
 *         'MM/DD/YYYY' | 'MM-DD-YYYY' | 'M/D/YYYY' | 'M-D-YYYY' |
 *         'MM/DD/YY' | 'MM-DD-YY' | 'M/D/YY' | 'M-D-YY' |
 *         'DD/MM/YYYY' | 'DD-MM-YYYY' | 'D/M/YYYY' | 'D-M-YYYY' |
 *         'DD/MM/YY' | 'DD-MM-YY' | 'D/M/YY' | 'D-M-YY' |
 *         'YYYY-MM-DD HH:mm:ss' | 'YYYY/MM/DD HH:mm:ss' |
 *         'YYYY-M-D HH:mm:ss' | 'YYYY/M/D HH:mm:ss' |
 *         'YY-MM-DD HH:mm:ss' | 'YY/MM/DD HH:mm:ss' |
 *         'YY-M-D HH:mm:ss' | 'YY/M/D HH:mm:ss' |
 *         'MM/DD/YYYY HH:mm:ss' | 'MM-DD-YYYY HH:mm:ss' |
 *         'M/D/YYYY HH:mm:ss' | 'M-D-YYYY HH:mm:ss' |
 *         'MM/DD/YY HH:mm:ss' | 'MM-DD-YY HH:mm:ss' |
 *         'M/D/YY HH:mm:ss' | 'M-D-YY HH:mm:ss' |
 *         'DD/MM/YYYY HH:mm:ss' | 'DD-MM-YYYY HH:mm:ss' |
 *         'D/M/YYYY HH:mm:ss' | 'D-M-YYYY HH:mm:ss' |
 *         'DD/MM/YY HH:mm:ss' | 'DD-MM-YY HH:mm:ss' |
 *         'D/M/YY HH:mm:ss' | 'D-M-YY HH:mm:ss'} format - The format string specifying the desired date format
 * @returns {string} The formatted date string
 */
export function formatDate(dateString, format = '') {
  if (!dateString) return null
  const date = new Date(dateString)
  const formatOptions = {
    YYYY: date.getFullYear(), // 2021
    YY: String(date.getFullYear()).slice(-2), // 21
    MMMM: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date), // January
    MMM: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date), // Jan
    EEE: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()], // Mon
    MM: String(date.getMonth() + 1).padStart(2, '0'), // 01
    M: date.getMonth() + 1, // 1
    DD: String(date.getDate()).padStart(2, '0'), // 01
    D: date.getDate(), // 1
    DO: getOrdinalSuffix(date.getDate()), // 1st
    HH: String(date.getHours()).padStart(2, '0'), // 15
    hh: String(date.getHours() % 12 || 12).padStart(2, '0'), // 07 or 12
    mm: String(date.getMinutes()).padStart(2, '0'), // 01
    ss: String(date.getSeconds()).padStart(2, '0'), // 01
    A: date.getHours() >= 12 ? 'PM' : 'AM'
  }

  return format.replace(/YYYY|YY|MMMM|MMM|EEE|MM|M|DD|DO|D|HH|hh|mm|ss|A/g, (match) => formatOptions[match])
}

/**
 * Returns the number with its ordinal suffix.
 * @param {number} i - The number to add the ordinal suffix to.
 * @returns {string} The number with its ordinal suffix.
 */
function getOrdinalSuffix(i) {
  const j = i % 10
  const k = i % 100
  if (j === 1 && k !== 11) {
    return i + 'st'
  }
  if (j === 2 && k !== 12) {
    return i + 'nd'
  }
  if (j === 3 && k !== 13) {
    return i + 'rd'
  }
  return i + 'th'
}

/**
 * Gets the user's current time zone.
 * @returns {string} The user's time zone.
 */
export function getUserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Validates if a given MIME type is supported based on a list of file extensions.
 *
 * @param {string} mimeType - The MIME type of the file to validate (e.g., "text/csv").
 * @param {string} supportedTypes - A comma-separated string of supported file extensions (e.g., ".csv, .xml, .jpg").
 * @returns {boolean} True if the MIME type is supported, false otherwise.
 *
 * @example
 * const isValid = validateFileType("text/csv", ".csv, .xml, .jpg, .pdf");
 * console.log(isValid); // true
 */
export function validateFileType(mimeType, supportedTypes) {
  const types = supportedTypes.split(',').map((t) => t.trim().slice(1))
  const lowerMimeType = mimeType.toLowerCase()
  const [type, subtype] = lowerMimeType.split('/')

  return types.some((t) => {
    if (t === 'xlsx' || t === 'sheet') {
      return lowerMimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    return type.includes(t) || subtype.includes(t)
  })
}

/**
 * Converts a given date to a human-readable relative format.//+
 *
 * @param {string|Date} inputDate - The date to convert. Can be a string or a Date object.
 * @returns {string} The relative time representation of the input date.
 *
 * @example
 * const relativeTime = timeAgo('2022-01-01T12:00:00Z');
 * console.log(relativeTime); // "2 months ago"
 *
 * @example
 * const relativeTime = timeAgo(new Date('2022-01-01T12:00:00Z'));
 * console.log(relativeTime); // "2 months ago"//+
 */
export function timeAgo(inputDate) {
  const now = new Date()
  const date = new Date(inputDate)
  const diffInSeconds = Math.floor((now - date) / 1000)

  const units = [
    { name: 'year', seconds: 31536000 },
    { name: 'month', seconds: 2592000 },
    { name: 'day', seconds: 86400 },
    { name: 'hour', seconds: 3600 },
    { name: 'minute', seconds: 60 }
  ]

  for (const unit of units) {
    const interval = Math.floor(diffInSeconds / unit.seconds)
    if (interval >= 1) {
      if (unit.name === 'day' && interval > 2) {
        return date.toLocaleDateString()
      }
      return `${interval} ${unit.name}${interval !== 1 ? 's' : ''} ago`
    }
  }

  return 'just now'
}
/**
 * Formats a number by converting it to a string, keeping integers as-is and limiting decimals to 2 places
 * @param {number} num - The number to format
 * @returns {string} The formatted number as a string
 */
export function formatNumber(num = 0) {
  return typeof num === 'number' ? (num % 1 === 0 ? num.toString() : num.toFixed(3)) : num
}
